import { useMutation } from "@tanstack/react-query";
import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { ReviewData } from "../../../models/ReviewData";
import DataSvc from "../../../services/dataSvc";

import "./styles.scss";

interface ReplyReviewModalProps {
  review: ReviewData | null;
  locationId: string;
  canReply?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ReplyReviewModal: React.FC<ReplyReviewModalProps> = ({
  review,
  locationId,
  canReply = true,
  onClose,
  onSuccess,
}) => {
  const { t: _t } = useTranslation();
  const t = useCallback((key: string) => _t(`replyModal.${key}`), [_t]);

  const [replyText, setReplyText] = useState("");

  const replyMutation = useMutation({
    mutationFn: (replyData: {
      locationId: string;
      reviewId: string;
      reply: string;
    }) =>
      DataSvc.postReplyReview({
        locationId: replyData.locationId,
        review_id: replyData.reviewId,
        reply: replyData.reply,
      }),
    onSuccess: () => {
      toast.success(t("replySuccess"));
      setReplyText("");
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      console.error("Reply failed:", error);
      toast.error(t("replyFailed"));
    },
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!replyText.trim()) {
        toast.error(t("pleaseEnterReply"));
        return;
      }

      if (!review) {
        toast.error(t("noReviewSelected"));
        return;
      }

      if (!canReply) {
        return;
      }

      replyMutation.mutate({
        locationId,
        reviewId: review.review_id,
        reply: replyText.trim(),
      });
    },
    [canReply, replyText, review, locationId, replyMutation, t]
  );

  const handleClose = useCallback(() => {
    setReplyText("");
    onClose();
  }, [onClose]);

  if (!review || !canReply) {
    return null;
  }

  return (
    <div className="modal-overlay reply-modal-overlay">
      <div className="modal-content reply-modal-content">
        <div className="modal-header">
          <h3>{t("replyToReview")}</h3>
          <button className="close-button" onClick={handleClose}>
            <i className="icons icon-close"></i>
          </button>
        </div>

        <div className="modal-body">
          <div className="review-info">
            <p className="reviewer-name">
              <strong>{review.reviewer_name || t("anonymous")}</strong>{" "}
              {t("reviewFrom")}
            </p>
            <div className="review-content">
              <div className="rating">
                {[...Array(5)].map((_, index) => (
                  <i
                    key={index}
                    className={`icons ${
                      index < (review.rating || 0)
                        ? "icon-star-filled"
                        : "icon-star-outline"
                    }`}
                  ></i>
                ))}
              </div>
              <p className="review-text">{review.comment}</p>
              <p className="review-date">
                {new Date(review.review_date).toLocaleDateString()}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="reply-form">
            <div className="form-group">
              <label htmlFor="reply-textarea" className="form-label">
                {t("yourReply")}
              </label>
              <textarea
                id="reply-textarea"
                className="form-textarea"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={t("enterYourReply")}
                rows={4}
                disabled={replyMutation.isLoading}
              />
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-border"
                onClick={handleClose}
                disabled={replyMutation.isLoading}
              >
                {t("cancel")}
              </button>
              <button
                type="submit"
                className="btn btn-blue submit-reply"
                disabled={replyMutation.isLoading || !replyText.trim()}
              >
                {replyMutation.isLoading ? t("submitting") : t("submit")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReplyReviewModal;
